import { Box } from "@mui/material"
import FilterIcon from "../../../icons/harmonization/FilterIcon"
import MultiSelectFilter from "./MultiSelectFilter"
import { useAppDispatch, useAppSelector } from "../../../util/hooks"
import { clearFilters, setDynamicFilters, updateDynamicFilter, updateOption } from "../mapping/MapSlice"
import { useEffect, useMemo } from "react"

const FilterComp = () => {
  const dispatch = useAppDispatch()
  const { filter, mappedData } = useAppSelector((state) => state.map)
  const { dataset1, dataset2 } = useAppSelector((state) => state.harmonization)

  const derivedKeys = useMemo(() => {
    const sample = mappedData[0]
    if (!sample) return { left: [], right: [] }
    const excluded = new Set(["id", "status", "similarity", "approval_status", "match_status"])
    const keys = Object.keys(sample as any).filter((k) => !(excluded as any).has(k))
    console.log("[FC] sample keys:", Object.keys(sample as any))
    console.log("[FC] keys after exclude:", keys)
    const hasSuffix = keys.some((k) => /_[lr]$/.test(k))
    console.log("[FC] hasSuffix:", hasSuffix)
    if (hasSuffix) {
      const left = keys.filter((k) => k.endsWith("_l"))
      const right = keys.filter((k) => k.endsWith("_r"))
      console.log("[FC] left keys:", left)
      console.log("[FC] right keys:", right)
      return {
        left,
        right,
      }
    }
    // Preserve order using selectedColumns; append remaining to right
    const ds1Cols = dataset1?.selectedColumns || []
    const ds2Cols = dataset2?.selectedColumns || []
    const used = new Set<string>()
    const pickByLabels = (labels: string[]) => {
      const picked: string[] = []
      labels.forEach((label) => {
        const found = keys.find(
          (k) => !used.has(k) && k.toLowerCase() === label.toLowerCase(),
        )
        if (found) {
          used.add(found)
          picked.push(found)
        }
      })
      return picked
    }
    const leftPicked = ds1Cols.length ? pickByLabels(ds1Cols) : []
    const rightPicked = ds2Cols.length ? pickByLabels(ds2Cols) : []
    const remaining = keys.filter((k) => !used.has(k))
    if (leftPicked.length === 0 && rightPicked.length === 0) {
      const mid = Math.floor(keys.length / 2)
      return { left: keys.slice(0, mid), right: keys.slice(mid) }
    }
    return { left: leftPicked, right: [...rightPicked, ...remaining] }
  }, [mappedData, dataset1?.selectedColumns, dataset2?.selectedColumns])

  useEffect(() => {
    const optionsByKey: Record<string, any[]> = {}
    const labelsByKey: Record<string, string> = {}
    const keyMapByComposite: Record<string, string> = {}

    const ds1 = dataset1?.selectedDataset || "dataset1"
    const ds2 = dataset2?.selectedDataset || "dataset2"
    console.log("[FC] ds1:", ds1, "ds2:", ds2)

    derivedKeys.left.forEach((rowKey) => {
      const values = mappedData
        .map((r: any) => (r[rowKey] === null || r[rowKey] === undefined ? "" : String(r[rowKey])))
        .filter((v: string) => v !== "")
      const base = rowKey.replace(/_[lr]$/, "")
      const display =
        dataset1?.selectedColumns?.find((c) => c.toLowerCase() === base.toLowerCase()) || base
      const compositeKey = `${ds1}_${display}`
      optionsByKey[compositeKey] = Array.from(new Set(values))
      labelsByKey[compositeKey] = display
      keyMapByComposite[compositeKey] = rowKey
    })

    derivedKeys.right.forEach((rowKey) => {
      const values = mappedData
        .map((r: any) => (r[rowKey] === null || r[rowKey] === undefined ? "" : String(r[rowKey])))
        .filter((v: string) => v !== "")
      const base = rowKey.replace(/_[lr]$/, "")
      const display =
        dataset2?.selectedColumns?.find((c) => c.toLowerCase() === base.toLowerCase()) || base
      const compositeKey = `${ds2}_${display}`
      optionsByKey[compositeKey] = Array.from(new Set(values))
      labelsByKey[compositeKey] = display
      keyMapByComposite[compositeKey] = rowKey
    })

    if (Object.keys(optionsByKey).length) {
      console.log("[FC] dynamic options keys:", Object.keys(optionsByKey))
      console.log("[FC] dynamic labels:", labelsByKey)
      console.log("[FC] dynamic keyMap:", keyMapByComposite)
      dispatch(setDynamicFilters({ optionsByKey, labelsByKey, keyMapByComposite }))
    }
  }, [derivedKeys, mappedData, dispatch, dataset1?.selectedColumns, dataset2?.selectedColumns, dataset1?.selectedDataset, dataset2?.selectedDataset])

  const handleChange = ({
    filterName,
    value,
  }: {
    filterName: string
    value: string[]
  }) => {
    if (filterName === "countries" || filterName === "categories" || filterName === "brand") {
    dispatch(updateOption({ filter: filterName, value }))
    } else {
      dispatch(updateDynamicFilter({ key: filterName, value }))
    }
  }

  const handleClearFilter = () => {
    dispatch(clearFilters())
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "center",
        flexWrap: "wrap",
        rowGap: 1,
      }}
    >
      <div className="flex gap-2 items-center">
        <FilterIcon />
        <p className="underline font-bold text-xs">Selection</p>
      </div>

      {Object.entries(filter.dynamicOptions).map(([key, options]) => {
        const label = filter.dynamicLabels[key] || key
        const selected = filter.dynamicSelected[key] || []
        return (
          <div key={key} className="flex gap-2 items-center">
        <MultiSelectFilter
              title={label}
              options={options}
              selectedValues={selected}
          onChange={(selectedValues) =>
                handleChange({ filterName: key, value: selectedValues })
          }
        />
      </div>
        )
      })}

      <button
        onClick={handleClearFilter}
        style={{
          background: "none",
          border: "none",
          color: "var(--primary-dp-color)",
          cursor: "pointer",
          fontSize: "12px",
          textDecoration: "underline",
        }}
      >
        Clear
      </button>
    </Box>
  )
}

export default FilterComp
